/**
 * Hardcoded AFL card pool for the first playable slice.
 *
 * Generic licensing tier per ADR 0004 — no real player names. Real
 * content and licensed upgrades land once the creator tool is built.
 *
 * Player stat totals follow the rarity budget (docs/afl-cards-and-costs.md):
 *   legendary 20 · epic 19 · rare 18 · uncommon 17 · common 16 · trash 15
 * spread across the 5 stats, each 0–5.
 *
 * `cost` on action/weather/stadium cards = the recycle cost to play them
 * (cards returned hand→deck). Authored by EFFECT strength, NOT rarity.
 */

import type { DescriptorKey } from './field.js';

export type Position = 'forward' | 'midfielder' | 'ruck' | 'back';

/**
 * Rarity tiers per docs/sports-tcg-rules.md. Lives on the card so it
 * flows through the viewer projection without a separate join.
 */
export type Rarity = 'legendary' | 'epic' | 'rare' | 'uncommon' | 'common' | 'trash';

/** Total stat-point budget per rarity (docs/afl-cards-and-costs.md). */
export const RARITY_STAT_BUDGET: Record<Rarity, number> = {
  legendary: 20,
  epic: 19,
  rare: 18,
  uncommon: 17,
  common: 16,
  trash: 15,
};

/**
 * The five core player stats per docs/sports-tcg-rules.md. Shared
 * across every TCG (AFL, cricket, future games) — sport-specific
 * actions choose which stat their resolution leans on.
 *
 * Raw values sit on a 0–5 scale. Negative effective values are
 * possible at runtime once stamina drain or modifiers apply.
 */
export type PlayerCardStats = {
  /** Fine motor / accuracy under pressure (kicks, handpasses, shots). */
  skill: number;
  /** Strength / leverage (contested marks, ruck taps, tackles, spoils). */
  power: number;
  /** Quick release / closing speed / reaction tempo. */
  speed: number;
  /** Decision-making, reading the play, set-piece IQ. */
  intelligence: number;
  /** Endurance — depletes across the match per the stamina rules. */
  stamina: number;
};

export type PlayerCard = {
  id: string;
  kind: 'player';
  name: string;
  /** Short archetype subtitle (e.g. "Tall forward", "Outside runner"). */
  archetype?: string;
  position: Position;
  stats: PlayerCardStats;
  rarity: Rarity;
  /** Italic flavour line shown on the preview modal. */
  flavor?: string;
};

export type ActionKind =
  | 'shot_on_goal'   // attacker: deliberate set shot — accuracy boost
  | 'snap'           // attacker: quick shot, usable when not set in position
  | 'long_bomb'      // attacker: extends shot range a zone, at an accuracy cost
  | 'checkside'      // attacker: relieves a wide / pocket shot angle
  | 'contested_mark' // attacker: rides a kick — boosts the kicker's marking
  | 'spoil'          // defender: in the marking contest — boosts the spoiler
  | 'smother'        // defender: kick-time window — docks the kicker's accuracy
  | 'handball_pass'  // attacker: makes a handpass safe from interception
  | 'dash'           // attacker: boosts the runner against the tackle
  | 'tackle'         // defender: boosts the committed tackler
  | 'torpedo'        // attacker: extends a kick's range (played WITH a kick)
  // SET / SUPPRESS / RELIEF actions — folded in from the former 'call' subclass
  // (2026-06-26). A spec action carries an `ActionSpec` and OVERRIDES rather than
  // nudges; its `bonus` is 0. New kinds so they never collide with the +x gates.
  | 'set_opponent_power'  // set the opposing contestant's power for one contest
  | 'set_own_speed'       // set your runner's speed for one run
  | 'suppress_tier'       // this contest ignores tier
  | 'suppress_position'   // this contest ignores position entirely
  | 'stamina_relief';     // ignore the stamina drain for one action

/**
 * When/where a modifier card may be played, gating which live contest it
 * feeds. Each maps to an actual decision point in the carrier model — there
 * are no orphan windows.
 */
export type ModifierWindow =
  | 'shot'          // on your own carrier shot (accuracy / range / angle)
  | 'kick'          // on your own carrier kick (Contested Mark; torpedo range)
  | 'mark_defense'  // responding to an opponent's kick — the marking contest
  | 'smother'       // the instant an opponent kicks — dock their accuracy
  | 'handpass'      // on your own handpass — beat the intercept
  | 'run'           // on your own run — beat the tackle
  | 'tackle';       // committing a tackler in the run-tackle window

export type ActionCard = {
  id: string;
  kind: 'action';
  name: string;
  actionKind: ActionKind;
  /** Where this modifier may be played (gates legality + UI surfacing). */
  window: ModifierWindow;
  /**
   * The PLAYER STAT this card modifies — and the stat its resolution actually
   * reads: shots lean on `skill` (Long Bomb swaps in `power`), marks on
   * `power`, the run-tackle on `speed`, the handpass on `intelligence`, and a
   * Smother docks the kicker's `skill`. The engine adds `bonus` to this stat.
   */
  modifiesStat: keyof PlayerCardStats;
  /** Plain-language effect shown on the card face (e.g. "Skill +2"). */
  effect: string;
  /** Amount added to `modifiesStat` in the resolution (a Smother's docks the
   *  opponent, so it is subtracted there). 0 for range-only torpedoes. */
  bonus: number;
  /**
   * Torpedo only: extra zones a kick may travel beyond the standard one.
   *   1 = standard torpedo (2 zones total) · 2 = rare torpedo (3 total,
   *   the only centre-line shot). Played together with a kick.
   */
  torpedoRange?: 1 | 2;
  /**
   * SET / SUPPRESS / RELIEF actions carry this base-layer spec (folded in from
   * the former 'call' subclass, 2026-06-26). Present → the card OVERRIDES for one
   * contest/action (sets a stat's base, or suppresses position/tier, or relieves
   * stamina) and `bonus` is 0; absent → a plain +x/−x modifier. The override
   * resolves at step 2 (before stamina) and any +x modifier actions still stack
   * ON TOP (see `baseStat` in index.ts). Re-uses the same set/suppress shapes as
   * Conditions, so the override path is shared.
   */
  spec?: ActionSpec;
  /** Recycle cost (cards returned hand→deck) to play this modifier. */
  cost: number;
  rarity: Rarity;
  flavor?: string;
};

export type WeatherCard = {
  id: string;
  kind: 'weather';
  name: string;
  /**
   * The 1–6 weather INTENSITY this card sets the match to (6 = most extreme).
   * The engine docks every kicker/shooter's SKILL by `scale − 1` (so 1 = clear,
   * no effect) — a real STAT modifier, never an action (tcg-card-conventions),
   * nullified by an indoor stadium. See docs/sports-tcg-rules.md §Weather.
   */
  scale: number;
  /** Plain-language effect shown on the card face (e.g. "Weather 5 · Skill −4"). */
  effect: string;
  /** Base recycle cost to play it (replacement cost is cumulative — engine). */
  cost: number;
  rarity: Rarity;
  flavor?: string;
};

export type StadiumCard = {
  id: string;
  kind: 'stadium';
  name: string;
  rating: 0 | 1 | 2 | 3 | 4 | 5;
  indoor: boolean;
  /**
   * The PLAYER STAT a home-ground installed venue lifts for the side that owns
   * it, and by how much (`bonus`, 0 for small grounds). Home-ground advantage
   * is shooting/kicking confidence → `skill` (see docs/tcg-card-conventions.md).
   */
  modifiesStat: keyof PlayerCardStats;
  bonus: number;
  /** Minimum recycle cost (the auction ante) to attempt to install it. */
  cost: number;
  rarity: Rarity;
  flavor?: string;
};

export type TimeCard = {
  id: string;
  kind: 'time';
  name: string;
  /**
   * 0 = day (no penalty), 1 = twilight (−1 skill), 2 = night (−2 skill). Time
   * only ADVANCES (day → twilight → night, never back). The skill penalty
   * (−index) bites SHOTS, nullified by an indoor (lit) stadium.
   */
  index: 0 | 1 | 2;
  /** Base recycle cost — day cheap, night dear. Altering an existing hour also
   *  pays the OUTGOING hour's cost (engine). */
  cost: number;
  rarity: Rarity;
  flavor?: string;
};

// ───────────────────────────────────────────────────────────────────────
//  Shared OVERRIDE spec — the base-layer operation behind BOTH Conditions
//  and Calls (the engine reads it in ONE place; see `baseStat` in index.ts).
//
//  An override is NOT a modifier. A SET replaces the printed base value for
//  the named stat (step 2 of the resolution order, BEFORE stamina and before
//  any +x/−x). A SUPPRESS touches no stat at all — it intercepts the contest
//  ladder and the dice position-bonus lookup as a separate rule branch.
// ───────────────────────────────────────────────────────────────────────

export type StatKey = keyof PlayerCardStats;

/**
 * SET base [stat] = value. Discards the printed number and feeds stamina /
 * modifiers / dice from the new base. Shared by stat-set Conditions and set
 * Calls — both resolve through the same base-layer path.
 */
export type StatSetSpec = { type: 'stat_set'; stat: StatKey; value: number };

/**
 * Position-rule SUPPRESSION — never touches the effective-stat stack.
 *   - `tier`     (Even Footing / Man on the Mark): the contest ladder skips the
 *     TIER comparison; in-position still beats out-of-position. Dice position
 *     bonuses are UNCHANGED.
 *   - `position` (Off the Magnets / Throw It on the Boot): the ladder skips BOTH
 *     in-position AND tier (contests resolve on stat → intelligence → tie), and
 *     the dice position bonus is zeroed for everyone (ruck/ruck-rover included).
 */
export type PositionSuppressMode = 'tier' | 'position';
export type PositionSuppressSpec = { type: 'position_suppress'; mode: PositionSuppressMode };

/** The override carried by a Conditions card (leaguewide, symmetric, persistent). */
export type ConditionSpec = StatSetSpec | PositionSuppressSpec;

export type ConditionCard = {
  id: string;
  kind: 'condition';
  name: string;
  /** The override this card installs in the universal Conditions slot. */
  condition: ConditionSpec;
  /** Plain-language effect shown on the card face (e.g. "Set Power = 2 · all players"). */
  effect: string;
  /** Recycle cost (cards returned hand→deck) to play it into the slot. */
  cost: number;
  rarity: Rarity;
  flavor?: string;
};

/**
 * The base-layer SET / SUPPRESS / RELIEF an Action may carry for ONE
 * contest/action (folded in from the former 'call' subclass, 2026-06-26). An
 * Action with a `spec` OVERRIDES rather than nudges, applies to a single
 * contest/action, then is spent — it never persists and never enters the
 * Conditions slot. Re-uses the SAME set / suppress shapes as Conditions (so the
 * override base-layer path is shared), plus a stamina-RELIEF variant (Second
 * Wind) that waives the drain penalty for one action — a relief, never a set.
 *   - stat_set on `own_player` / `opponent_player` — set that contestant's base
 *     (Second Effort, Chopped Off).
 *   - position_suppress on the `contest` — ignore tier / position for it
 *     (Man on the Mark, Throw It on the Boot).
 *   - stamina_relief on `own_player` — waive the drain for one action (Second Wind).
 * A +x/−x modifier action still stacks ON TOP (step 4) of whatever base a set
 * establishes (step 2): set Power = 2 then a +2 rider resolves at 4.
 */
export type ActionSpec =
  | { type: 'stat_set'; stat: StatKey; value: number; target: 'own_player' | 'opponent_player' }
  | { type: 'position_suppress'; mode: PositionSuppressMode; target: 'contest' }
  | { type: 'stamina_relief'; target: 'own_player' };

export type AnyCard =
  | PlayerCard
  | ActionCard
  | WeatherCard
  | StadiumCard
  | TimeCard
  | ConditionCard;

// Card ids are kept as `player_<role>_<n>` so saved decks survive
// renames. Display copy can churn freely.
export const playerCards: readonly PlayerCard[] = [
  // ─── Forwards ──────────────────────────────────────────────────────
  { id: 'player_forward_1', kind: 'player', name: 'The Spearhead', archetype: 'Key forward', position: 'forward', rarity: 'rare', stats: { skill: 5, power: 5, speed: 2, intelligence: 3, stamina: 3 }, flavor: '"Just give me the ball at fifty and step back."' },
  { id: 'player_forward_2', kind: 'player', name: 'The Crumber', archetype: 'Small forward', position: 'forward', rarity: 'uncommon', stats: { skill: 4, power: 2, speed: 5, intelligence: 3, stamina: 3 }, flavor: 'Lives in the goalsquare grass.' },
  { id: 'player_forward_3', kind: 'player', name: 'The Goal Sneak', archetype: 'Resting forward', position: 'forward', rarity: 'uncommon', stats: { skill: 4, power: 2, speed: 4, intelligence: 4, stamina: 3 }, flavor: 'Always two steps behind his man, then one in front.' },
  { id: 'player_forward_4', kind: 'player', name: 'The Hall-of-Famer', archetype: 'Once-in-a-generation forward', position: 'forward', rarity: 'legendary', stats: { skill: 5, power: 5, speed: 3, intelligence: 4, stamina: 3 }, flavor: 'They named the grandstand after him before he retired.' },
  { id: 'player_forward_5', kind: 'player', name: 'The Centre-Half', archetype: 'Key marking forward', position: 'forward', rarity: 'rare', stats: { skill: 4, power: 5, speed: 3, intelligence: 3, stamina: 3 }, flavor: 'The hardest position on the ground, and he owns it.' },
  { id: 'player_forward_6', kind: 'player', name: 'The Pocket Snipe', archetype: 'Forward pocket', position: 'forward', rarity: 'uncommon', stats: { skill: 4, power: 2, speed: 5, intelligence: 3, stamina: 3 }, flavor: 'Banana from the boundary, every time.' },
  { id: 'player_forward_7', kind: 'player', name: 'The Flanker', archetype: 'Half-forward flank', position: 'forward', rarity: 'common', stats: { skill: 4, power: 2, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'Up and back, up and back.' },
  { id: 'player_forward_8', kind: 'player', name: 'The Lead-Up', archetype: 'Leading half-forward', position: 'forward', rarity: 'uncommon', stats: { skill: 4, power: 3, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'Times the lead so the kick only has to be honest.' },
  { id: 'player_forward_9', kind: 'player', name: 'The Deep Target', archetype: 'Deep forward', position: 'forward', rarity: 'common', stats: { skill: 3, power: 4, speed: 3, intelligence: 3, stamina: 3 }, flavor: 'Plants himself on the goalline and waits.' },
  { id: 'player_forward_10', kind: 'player', name: 'The Park Footballer', archetype: 'Journeyman forward', position: 'forward', rarity: 'trash', stats: { skill: 3, power: 3, speed: 3, intelligence: 3, stamina: 3 }, flavor: 'Tries hard. Bless him.' },
  { id: 'player_util_1', kind: 'player', name: 'The Swingman', archetype: 'Utility', position: 'forward', rarity: 'uncommon', stats: { skill: 4, power: 3, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'Wherever the coach points.' },
  { id: 'player_small_1', kind: 'player', name: 'The Livewire', archetype: 'Small utility', position: 'forward', rarity: 'common', stats: { skill: 3, power: 2, speed: 5, intelligence: 3, stamina: 3 }, flavor: 'Buzzes around the packs all day.' },

  // ─── Midfielders ───────────────────────────────────────────────────
  { id: 'player_mid_1', kind: 'player', name: 'The Engine', archetype: 'Inside mid', position: 'midfielder', rarity: 'rare', stats: { skill: 3, power: 4, speed: 3, intelligence: 4, stamina: 4 }, flavor: 'First to the contest, last to leave it.' },
  { id: 'player_mid_2', kind: 'player', name: 'The Wing', archetype: 'Outside runner', position: 'midfielder', rarity: 'uncommon', stats: { skill: 3, power: 2, speed: 5, intelligence: 3, stamina: 4 }, flavor: 'Runs the line for the full ninety minutes.' },
  { id: 'player_mid_3', kind: 'player', name: 'The Ball-Magnet', archetype: 'Inside-outside mid', position: 'midfielder', rarity: 'epic', stats: { skill: 4, power: 4, speed: 4, intelligence: 4, stamina: 3 }, flavor: 'Forty touches and never breaks a sweat.' },
  { id: 'player_mid_4', kind: 'player', name: 'The Mosquito', archetype: 'Crumbing mid', position: 'midfielder', rarity: 'common', stats: { skill: 3, power: 2, speed: 5, intelligence: 3, stamina: 3 }, flavor: 'You can hear him before you see him.' },
  { id: 'player_mid_5', kind: 'player', name: 'The Ball-Winner', archetype: 'Elite inside mid', position: 'midfielder', rarity: 'epic', stats: { skill: 4, power: 4, speed: 4, intelligence: 4, stamina: 3 }, flavor: 'Wins his own ball and yours.' },
  { id: 'player_mid_6', kind: 'player', name: 'The Linkman', archetype: 'Outside wing', position: 'midfielder', rarity: 'common', stats: { skill: 3, power: 2, speed: 4, intelligence: 3, stamina: 4 }, flavor: 'Always an option on the chain.' },
  { id: 'player_mid_7', kind: 'player', name: 'The Grunt', archetype: 'Inside midfield', position: 'midfielder', rarity: 'common', stats: { skill: 3, power: 3, speed: 3, intelligence: 4, stamina: 3 }, flavor: 'Does the unseen work.' },
  { id: 'player_mid_8', kind: 'player', name: 'The Onballer', archetype: 'Two-way mid', position: 'midfielder', rarity: 'uncommon', stats: { skill: 3, power: 3, speed: 4, intelligence: 4, stamina: 3 }, flavor: 'Both ends, all day.' },
  { id: 'player_mid_9', kind: 'player', name: 'The Scrapper', archetype: 'Crumbing rover', position: 'midfielder', rarity: 'trash', stats: { skill: 3, power: 2, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'All effort, raw edges.' },

  // ─── Rucks ─────────────────────────────────────────────────────────
  { id: 'player_ruck_1', kind: 'player', name: 'The Tower', archetype: 'Tap ruck', position: 'ruck', rarity: 'rare', stats: { skill: 2, power: 5, speed: 2, intelligence: 4, stamina: 5 }, flavor: 'Two metres of leverage at every centre bounce.' },
  { id: 'player_ruck_2', kind: 'player', name: 'The Mobile Ruck', archetype: 'Following ruck', position: 'ruck', rarity: 'uncommon', stats: { skill: 3, power: 4, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'Tap it, then beat the rover to it.' },
  { id: 'player_ruck_3', kind: 'player', name: 'The Big Man', archetype: 'Contest ruck', position: 'ruck', rarity: 'uncommon', stats: { skill: 2, power: 5, speed: 2, intelligence: 4, stamina: 4 }, flavor: 'Wins the tap, owns the square.' },
  { id: 'player_ruck_4', kind: 'player', name: 'The Relief Ruck', archetype: 'Relief ruck', position: 'ruck', rarity: 'common', stats: { skill: 2, power: 4, speed: 3, intelligence: 4, stamina: 3 }, flavor: 'Gives the big fella a chop-out.' },

  // ─── Backs ─────────────────────────────────────────────────────────
  { id: 'player_back_1', kind: 'player', name: 'The Lockdown', archetype: 'Key defender', position: 'back', rarity: 'uncommon', stats: { skill: 3, power: 5, speed: 2, intelligence: 4, stamina: 3 }, flavor: 'Reads the play three kicks ahead.' },
  { id: 'player_back_2', kind: 'player', name: 'The Tagger', archetype: 'Small defender', position: 'back', rarity: 'common', stats: { skill: 3, power: 3, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'Wherever you go, there he is.' },
  { id: 'player_back_3', kind: 'player', name: 'The Sweeper', archetype: 'Rebounding defender', position: 'back', rarity: 'uncommon', stats: { skill: 4, power: 3, speed: 4, intelligence: 3, stamina: 3 }, flavor: 'First option out of defence, every time.' },
  { id: 'player_back_4', kind: 'player', name: 'The Hardhead', archetype: 'Stopper', position: 'back', rarity: 'common', stats: { skill: 2, power: 5, speed: 2, intelligence: 4, stamina: 3 }, flavor: 'Body on body, all day.' },
  { id: 'player_back_5', kind: 'player', name: 'The Anchor', archetype: 'Tall key back', position: 'back', rarity: 'rare', stats: { skill: 3, power: 5, speed: 2, intelligence: 5, stamina: 3 }, flavor: 'The last line, and he knows it.' },
  { id: 'player_back_6', kind: 'player', name: 'The Rebounder', archetype: 'Rebounding flanker', position: 'back', rarity: 'uncommon', stats: { skill: 4, power: 2, speed: 5, intelligence: 3, stamina: 3 }, flavor: 'Intercept, gather, gone.' },
  { id: 'player_back_7', kind: 'player', name: 'The Back Pocket', archetype: 'Lockdown pocket', position: 'back', rarity: 'common', stats: { skill: 3, power: 3, speed: 3, intelligence: 4, stamina: 3 }, flavor: 'Quiet, reliable, unbeaten one-on-one.' },
  { id: 'player_back_8', kind: 'player', name: 'The Shadow', archetype: 'Small defender', position: 'back', rarity: 'common', stats: { skill: 2, power: 3, speed: 4, intelligence: 4, stamina: 3 }, flavor: "He'll be there when the ball lands." },
  { id: 'player_back_9', kind: 'player', name: 'The Plodder', archetype: 'Honest backman', position: 'back', rarity: 'trash', stats: { skill: 3, power: 3, speed: 2, intelligence: 4, stamina: 3 }, flavor: 'Slow, but he never gets beaten twice.' },
  { id: 'player_back_10', kind: 'player', name: 'The Journeyman', archetype: 'Utility back', position: 'back', rarity: 'common', stats: { skill: 3, power: 3, speed: 3, intelligence: 4, stamina: 3 }, flavor: 'Four clubs, four hundred games.' },
  { id: 'player_util_2', kind: 'player', name: 'The Handyman', archetype: 'Utility', position: 'back', rarity: 'common', stats: { skill: 3, power: 3, speed: 3, intelligence: 3, stamina: 4 }, flavor: 'Fills the hole, whatever the hole is.' },
  { id: 'player_tall_1', kind: 'player', name: 'The Beanpole', archetype: 'Key-position swing', position: 'back', rarity: 'uncommon', stats: { skill: 3, power: 5, speed: 2, intelligence: 4, stamina: 3 }, flavor: 'Both ends of the ground, neither well-liked by his opponents.' },
];

// Each card MODIFIES A REAL STAT (the one its resolution reads) by `bonus`.
// `effect` is the card-face text. Numbers are starting values, tuned in
// playtest. Gating (Snap = live ball, Banana = pocket mark, torpedo timing)
// lives in the engine.
export const actionCards: readonly ActionCard[] = [
  // ─── Shot modifiers — played on your own carrier shot ──────────────
  { id: 'action_shot_on_goal', kind: 'action', name: 'Set Shot', actionKind: 'shot_on_goal', window: 'shot', modifiesStat: 'skill', effect: 'Skill +2', bonus: 2, cost: 1, rarity: 'rare', flavor: 'Set yourself, breathe, kick.' },
  { id: 'action_snap', kind: 'action', name: 'Snap Shot', actionKind: 'snap', window: 'shot', modifiesStat: 'skill', effect: 'Skill +2 — live ball only', bonus: 2, cost: 1, rarity: 'uncommon', flavor: 'No time to think, just swing the boot.' },
  { id: 'action_long_bomb', kind: 'action', name: 'Long Bomb', actionKind: 'long_bomb', window: 'shot', modifiesStat: 'power', effect: 'Power +2 — shoot from the centre', bonus: 2, cost: 2, rarity: 'uncommon', flavor: 'From sixty out, into the wind, why not.' },
  { id: 'action_checkside', kind: 'action', name: 'Checkside Banana', actionKind: 'checkside', window: 'shot', modifiesStat: 'skill', effect: 'Skill +2 — pocket mark only', bonus: 2, cost: 1, rarity: 'rare', flavor: 'Spins it back across the face, against every coach\'s advice.' },
  // ─── Kick rider — played WITH your kick ─────────────────────────────
  { id: 'action_mark_attempt', kind: 'action', name: 'Contested Mark', actionKind: 'contested_mark', window: 'kick', modifiesStat: 'power', effect: 'Power +2', bonus: 2, cost: 1, rarity: 'rare', flavor: 'Above the pack, fingertips first.' },
  // ─── Marking-contest defence — responding to an opponent kick ──────
  { id: 'action_spoil', kind: 'action', name: 'Spoil', actionKind: 'spoil', window: 'mark_defense', modifiesStat: 'power', effect: 'Power +2', bonus: 2, cost: 1, rarity: 'uncommon', flavor: 'Punch through. Worry about possession later.' },
  // ─── Smother — the instant the opponent kicks (docks the kicker) ───
  { id: 'action_smother', kind: 'action', name: 'Smother', actionKind: 'smother', window: 'smother', modifiesStat: 'skill', effect: "Kicker's skill −2", bonus: 2, cost: 1, rarity: 'uncommon', flavor: 'Hand on boot before the kick lands.' },
  // ─── Disposal aids ─────────────────────────────────────────────────
  { id: 'action_handball_pass', kind: 'action', name: 'Handball Pass', actionKind: 'handball_pass', window: 'handpass', modifiesStat: 'intelligence', effect: 'Intelligence +3', bonus: 3, cost: 1, rarity: 'common', flavor: 'Quick hands keep the chain alive.' },
  { id: 'action_dash', kind: 'action', name: 'Dash and Carry', actionKind: 'dash', window: 'run', modifiesStat: 'speed', effect: 'Speed +2', bonus: 2, cost: 1, rarity: 'common', flavor: 'Bounce, bounce, bounce — go go go.' },
  // ─── Tackle — committing a tackler in the run window ───────────────
  { id: 'action_tackle', kind: 'action', name: 'Tackle', actionKind: 'tackle', window: 'tackle', modifiesStat: 'speed', effect: 'Speed +2', bonus: 2, cost: 1, rarity: 'common', flavor: 'Wrap them up, hold them in.' },
  // ─── Torpedo — shot range; cheaper after the siren ─────────────────
  { id: 'action_torpedo', kind: 'action', name: 'Torpedo', actionKind: 'torpedo', window: 'kick', modifiesStat: 'skill', effect: 'Shoot one zone further', bonus: 0, torpedoRange: 1, cost: 2, rarity: 'rare', flavor: 'Drop it on the laces and let it scream.' },
  { id: 'action_big_torpedo', kind: 'action', name: 'Big Torpedo', actionKind: 'torpedo', window: 'kick', modifiesStat: 'skill', effect: 'Shoot from the centre', bonus: 0, torpedoRange: 2, cost: 4, rarity: 'legendary', flavor: 'Sixty-five metres on the full. Once a season, maybe.' },
  // ─── SET / SUPPRESS / RELIEF actions (folded in from the former 'call'
  //     subclass, 2026-06-26). Each OVERRIDES for ONE contest/action via `spec`
  //     (bonus 0) then is spent; a +x modifier action still stacks on top of a
  //     set base. Ids kept `call_*` (stable tokens — saved decks rely on them).
  { id: 'call_chopped_off', kind: 'action', name: 'Chopped Off', actionKind: 'set_opponent_power', window: 'mark_defense', modifiesStat: 'power', effect: 'Set their Power = 2 — this contest', bonus: 0, spec: { type: 'stat_set', stat: 'power', value: 2, target: 'opponent_player' }, cost: 1, rarity: 'uncommon', flavor: 'Pin the arms, kill the leap.' },
  { id: 'call_second_effort', kind: 'action', name: 'Second Effort', actionKind: 'set_own_speed', window: 'run', modifiesStat: 'speed', effect: 'Set your Speed = 4 — this run', bonus: 0, spec: { type: 'stat_set', stat: 'speed', value: 4, target: 'own_player' }, cost: 1, rarity: 'uncommon', flavor: 'Up off the deck and gone before the tackle lands.' },
  { id: 'call_man_on_the_mark', kind: 'action', name: 'Man on the Mark', actionKind: 'suppress_tier', window: 'mark_defense', modifiesStat: 'power', effect: 'This contest ignores tier — power decides', bonus: 0, spec: { type: 'position_suppress', mode: 'tier', target: 'contest' }, cost: 1, rarity: 'rare', flavor: 'Stand the mark, square him up — no advantage to the gun.' },
  { id: 'call_throw_it_on_the_boot', kind: 'action', name: 'Throw It on the Boot', actionKind: 'suppress_position', window: 'mark_defense', modifiesStat: 'power', effect: 'This contest ignores position entirely — power decides', bonus: 0, spec: { type: 'position_suppress', mode: 'position', target: 'contest' }, cost: 2, rarity: 'rare', flavor: 'No time to find a target. Just throw it on the boot.' },
  { id: 'call_second_wind', kind: 'action', name: 'Second Wind', actionKind: 'stamina_relief', window: 'run', modifiesStat: 'stamina', effect: 'Ignore the stamina drain — this action', bonus: 0, spec: { type: 'stamina_relief', target: 'own_player' }, cost: 1, rarity: 'uncommon', flavor: 'One last burst from somewhere.' },
];

// A weather card SETS the match weather intensity (1–6); the engine docks every
// kicker/shooter's SKILL by scale−1 (a STAT modifier, not an action — see
// tcg-card-conventions). Starting weather is rolled (lowest of two d6).
export const weatherCards: readonly WeatherCard[] = [
  { id: 'weather_clear', kind: 'weather', name: 'Clear Sky', scale: 1, effect: 'Clears it — Skill ±0', cost: 1, rarity: 'common', flavor: 'A perfect day for footy.' },
  { id: 'weather_overcast', kind: 'weather', name: 'Overcast', scale: 3, effect: 'Skill −2', cost: 1, rarity: 'uncommon', flavor: 'Heavy ball, heavy legs.' },
  { id: 'weather_windy', kind: 'weather', name: 'Driving Rain', scale: 4, effect: 'Skill −3', cost: 2, rarity: 'uncommon', flavor: 'Sideways rain, and it stings.' },
  { id: 'weather_wet', kind: 'weather', name: 'Wet Track', scale: 5, effect: 'Skill −4', cost: 2, rarity: 'rare', flavor: 'Greasy ball, greasy hands. Fumble city.' },
];

// A time-of-day card ADVANCES the hour (day → twilight → night, never back).
// Failing light docks every SHOT's skill by the time index (day 0, twilight −1,
// night −2), nullified by an indoor (lit) stadium. Day is cheap, night dear;
// altering an existing hour also costs the OUTGOING hour (engine). See
// docs/sports-tcg-rules.md §Time.
export const timeCards: readonly TimeCard[] = [
  { id: 'time_day', kind: 'time', name: 'Day', index: 0, cost: 1, rarity: 'common', flavor: 'Sun overhead — every post in view.' },
  { id: 'time_twilight', kind: 'time', name: 'Twilight', index: 1, cost: 2, rarity: 'uncommon', flavor: 'Long shadows; the ball turns slippery to read.' },
  { id: 'time_night', kind: 'time', name: 'Night', index: 2, cost: 3, rarity: 'rare', flavor: 'Under lights — the far pocket is a guess.' },
];

// Stadiums are PERMANENT once installed and apply to ALL players, BOTH sides
// (a symmetric passive — see docs/sports-tcg-rules.md §Stadiums). The effect is
// a real STAT modifier (modifiesStat + bonus), folded into that stat the
// resolution reads. Because a stadium can't be modified or removed and can
// swing the whole match, it is SUPER EXPENSIVE to play (the auction ante).
// `bonus: 0` = a deliberate do-nothing venue whose only value is denying the
// opponent a stadium. Costs/ratings below are proposals — tune freely.
// NOTE: stamina is designed but not yet wired (docs/sports-tcg-rules.md
// §Stamina), so The Hallowed Turf is a forward-looking buffer — inert until the
// stamina-interval engine lands.
export const stadiumCards: readonly StadiumCard[] = [
  { id: 'stadium_mcg', kind: 'stadium', name: 'The Hallowed Turf', rating: 5, indoor: false, modifiesStat: 'stamina', bonus: 1, cost: 6, rarity: 'legendary', flavor: 'Eighty thousand voices. Legs that never tire.' },
  { id: 'stadium_twilight', kind: 'stadium', name: 'Twilight Stadium', rating: 4, indoor: false, modifiesStat: 'skill', bonus: 1, cost: 6, rarity: 'epic', flavor: 'Perfect light, true bounce — every kick a touch sharper.' },
  { id: 'stadium_quagmire', kind: 'stadium', name: 'The Quagmire', rating: 1, indoor: false, modifiesStat: 'skill', bonus: -1, cost: 5, rarity: 'rare', flavor: 'A mud heap. Nobody kicks straight here.' },
  { id: 'stadium_dome', kind: 'stadium', name: 'The Dome', rating: 4, indoor: true, modifiesStat: 'skill', bonus: 0, cost: 5, rarity: 'rare', flavor: 'No wind, no rain — just fifty thousand voices in a tin can.' },
  { id: 'stadium_suburban', kind: 'stadium', name: 'Suburban Oval', rating: 2, indoor: false, modifiesStat: 'skill', bonus: 0, cost: 4, rarity: 'common', flavor: 'Two pies, three beers, one terrace — and nothing to write home about.' },
  { id: 'stadium_reserve', kind: 'stadium', name: 'Local Reserve', rating: 1, indoor: false, modifiesStat: 'stamina', bonus: -1, cost: 4, rarity: 'common', flavor: 'Patchy grass, a portable, and a long walk to the rooms.' },
  { id: 'stadium_bush', kind: 'stadium', name: 'Bush Paddock', rating: 0, indoor: false, modifiesStat: 'stamina', bonus: -2, cost: 5, rarity: 'uncommon', flavor: 'Cow paddock with goalposts. Everyone leaves on their hands and knees.' },
];

// Conditions are a replaceable, symmetric, LEAGUEWIDE passive — one universal
// slot, Pokémon-stadium style (a new one discards the old). A stat-set Condition
// overrides a base stat for ALL players, both sides (step 2 of the resolution
// order); a position Condition suppresses position rules. Launch values are
// SET 2 / SET 3 only. See docs/sports-tcg-rules.md §Conditions.
export const conditionCards: readonly ConditionCard[] = [
  { id: 'condition_strict_adjudication', kind: 'condition', name: 'Strict Adjudication', condition: { type: 'stat_set', stat: 'power', value: 2 }, effect: 'Set Power = 2 · all players', cost: 3, rarity: 'rare', flavor: 'No leverage, no holding — every contest off the same base.' },
  { id: 'condition_flood', kind: 'condition', name: 'Flood', condition: { type: 'stat_set', stat: 'speed', value: 2 }, effect: 'Set Speed = 2 · all players', cost: 3, rarity: 'rare', flavor: 'Numbers behind the ball. Nobody breaks away.' },
  { id: 'condition_rolling_interchange', kind: 'condition', name: 'Rolling Interchange', condition: { type: 'stat_set', stat: 'stamina', value: 3 }, effect: 'Set Stamina = 3 · all players', cost: 3, rarity: 'uncommon', flavor: 'Fresh legs through the gate all day — everyone runs out the game.' },
  { id: 'condition_even_footing', kind: 'condition', name: 'Even Footing', condition: { type: 'position_suppress', mode: 'tier' }, effect: 'Contests ignore tier — position vs out-of-position only', cost: 4, rarity: 'epic', flavor: 'A greasy deck levels the specialist and the journeyman.' },
  { id: 'condition_off_the_magnets', kind: 'condition', name: 'Off the Magnets', condition: { type: 'position_suppress', mode: 'position' }, effect: 'Positions do not apply — contests on stat alone', cost: 4, rarity: 'epic', flavor: 'The magnets are off the board. It is just twenty-two blokes and a footy.' },
];

// NOTE: the former Call cards now live in `actionCards` above as SET / SUPPRESS /
// RELIEF actions (the `call_*` ids, each carrying an `ActionSpec`). There is no
// separate Call card type — a "set" action sets the base; +x modifiers still
// stack on top. See docs/sports-tcg-rules.md §"Set / suppress / relief actions".

export const allCards: readonly AnyCard[] = [
  ...playerCards,
  ...actionCards,
  ...weatherCards,
  ...stadiumCards,
  ...timeCards,
  ...conditionCards,
];

export const cardsById: ReadonlyMap<string, AnyCard> = new Map(
  allCards.map((card) => [card.id, card] as const),
);

/**
 * Maps each player card to its position descriptor (see field.ts /
 * docs/afl-position-tiers.md). Kept as a single table so the assignments
 * can be retuned in one place without touching the card literals.
 */
export const PLAYER_POSITION_DESCRIPTOR: Record<string, DescriptorKey> = {
  // Forwards
  player_forward_1: 'full_forward',
  player_forward_2: 'small_forward',
  player_forward_3: 'forward',
  player_forward_4: 'tall_forward',
  player_forward_5: 'centre_half_forward',
  player_forward_6: 'forward_pocket',
  player_forward_7: 'hf_flank',
  player_forward_8: 'half_forward',
  player_forward_9: 'deep_forward',
  player_forward_10: 'forward',
  player_util_1: 'utility',
  player_small_1: 'small',
  // Midfielders
  player_mid_1: 'centre',
  player_mid_2: 'wing',
  player_mid_3: 'midfield',
  player_mid_4: 'rover',
  player_mid_5: 'centre',
  player_mid_6: 'wing',
  player_mid_7: 'midfield',
  player_mid_8: 'midfield',
  player_mid_9: 'rover',
  // Rucks
  player_ruck_1: 'ruck',
  player_ruck_2: 'ruck_rover',
  player_ruck_3: 'ruck',
  player_ruck_4: 'ruck',
  // Backs
  player_back_1: 'full_back',
  player_back_2: 'small_defender',
  player_back_3: 'half_back',
  player_back_4: 'tall_defender',
  player_back_5: 'centre_half_back',
  player_back_6: 'hb_flank',
  player_back_7: 'back_pocket',
  player_back_8: 'small_defender',
  player_back_9: 'deep_back',
  player_back_10: 'backman',
  player_util_2: 'utility',
  player_tall_1: 'tall',
};

/** Position descriptor for a card id, or undefined for non-players. */
export function descriptorForCard(cardId: string): DescriptorKey | undefined {
  return PLAYER_POSITION_DESCRIPTOR[cardId];
}

/** Recycle cost to play a card (0 for players / uncosted cards). */
export function cardCost(cardId: string): number {
  const card = cardsById.get(cardId);
  if (!card) return 0;
  return 'cost' in card && typeof card.cost === 'number' ? card.cost : 0;
}

/** A +x/−x MODIFIER action — NOT a set/suppress/relief spec action. The `+x`
 *  rider windows (Spoil / Dash / Contested Mark / Tackle / shot mods) must
 *  filter to these so a spec action is never offered as a modifier. */
export function isModifierAction(card: AnyCard | undefined): card is ActionCard {
  return !!card && card.kind === 'action' && !card.spec;
}

/** A SET / SUPPRESS / RELIEF action — carries an `ActionSpec` (was a 'call'). */
export function isSpecAction(card: AnyCard | undefined): card is ActionCard {
  return !!card && card.kind === 'action' && !!card.spec;
}

/** Build the starting deck for a team. Shared generic pool. */
export function buildStartingDeck(): readonly AnyCard[] {
  return [
    ...playerCards,
    ...actionCards,
    ...weatherCards,
    ...stadiumCards,
    ...timeCards,
    ...conditionCards,
  ];
}
